import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { Control } from 'react-hook-form';
import { IoListOutline } from 'react-icons/io5';
import { AiOutlineAppstore } from 'react-icons/ai';
import Search from '@/components/Common/Search';
import Select from '@/components/Common/Select';
import DateRangePicker from '@/components/Common/DateRangePicker';
import AddBusinessButton from './AddBusinessConfirmModal';
import { useGetTaggingContexts } from '@/apis/agency/taggings/hook';
import { buildNameSelectOptionsFromContexts } from './businessTags';
import { useMemo } from 'react';

export type BusinessesViewMode = 'list' | 'insights';

export type BusinessesFilterForm = {
  status: string;
  tags: string[];
  plan_started_at_in: string;
};

interface BusinessesHeaderProps {
  viewMode: BusinessesViewMode;
  onViewModeChange: (mode: BusinessesViewMode) => void;
  searchText: string | null;
  onSearchChange: (value: string | null) => void;
  control: Control<BusinessesFilterForm>;
}

export default function BusinessesHeader({
  viewMode,
  onViewModeChange,
  searchText,
  onSearchChange,
  control
}: BusinessesHeaderProps) {
  const theme = useTheme();
  const { data: contexts } = useGetTaggingContexts();
  const tagOptions = useMemo(() => buildNameSelectOptionsFromContexts(contexts), [contexts]);

  return (
    <Box mb={2}>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={viewMode === 'list' ? 2 : 0}>
        <Typography variant='h6' fontWeight={500}>
          Businesses
        </Typography>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, value) => value && onViewModeChange(value)}
          sx={{
            backgroundColor: '#fff',
            borderRadius: '999px',
            padding: '3px',
            display: 'flex',
            gap: '2px',
            flexShrink: 0,

            '& .MuiToggleButton-root': {
              border: 'none',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 500,
              color: '#6B7280',
              borderRadius: '999px',
              padding: '6px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            },

            '& .Mui-selected': {
              backgroundColor: `${theme.palette.primary.main} !important`,
              color: '#fff !important',
              boxShadow: '0px 2px 6px rgba(0,0,0,0.12)'
            }
          }}
        >
          <ToggleButton value='list'>
            <IoListOutline size={16} />
            List
          </ToggleButton>
          <ToggleButton value='insights'>
            <AiOutlineAppstore size={16} />
            Insights
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'list' && (
        <Box
          display='flex'
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={2}
          width='100%'
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={2}
            flex={1}
            width='100%'
            minWidth={0}
            alignItems='stretch'
          >
            <Box flex={1} minWidth={0} width='100%'>
              <Search
                placeholder='Search business'
                searchText={searchText}
                setSearchText={onSearchChange}
                size='small'
                fullWidth
              />
            </Box>
            <Box flex={1} minWidth={0} width='100%'>
              <Select
                name='tags'
                label='Tags'
                sortOrder='none'
                control={control}
                visibleCount={0}
                fullWidth
                multiple
                options={tagOptions}
                size='small'
                sx={{ background: '#fff', maxHeight: '40px', width: '100%' }}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 360 }
                  }
                }}
              />
            </Box>
            <Box flex={1} minWidth={0} width='100%'>
              <Select
                name='status'
                label='Status'
                sortOrder='none'
                control={control}
                fullWidth
                options={[
                  { value: ' ', label: 'All' },
                  { value: '0', label: 'Active' },
                  { value: '2', label: 'Inactive' },
                  { value: '1', label: 'Past Due' },
                  { value: '3', label: 'Archived' }
                ]}
                size='small'
                sx={{ background: '#fff', maxHeight: '40px', width: '100%' }}
              />
            </Box>
            <Box flex={1} minWidth={0} width='100%'>
              <DateRangePicker
                name='plan_started_at_in'
                label='Billing Period'
                control={control}
                sx={{ height: '40px', background: '#fff' }}
              />
            </Box>
          </Stack>

          <Box flexShrink={0} alignSelf={{ xs: 'stretch', md: 'center' }}>
            <AddBusinessButton />
          </Box>
        </Box>
      )}
    </Box>
  );
}
